import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  FullPageSpinner,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Tab,
  Tabs,
  useTabParam,
} from '@structural-climbing/ui'
import { competitionBoards, disciplineForSection } from '@structural-climbing/validation'
import { useGymContext } from '../contexts/GymContext'
import { CompetitionFormModal } from '../features/competitions/CompetitionFormModal'
import { RoundsTab } from '../features/competitions/RoundsTab'
import { SessionsTab } from '../features/competitions/SessionsTab'
import { CategoriesTab } from '../features/competitions/CategoriesTab'
import { RoutesTab } from '../features/competitions/RoutesTab'
import { CompetitorsTab } from '../features/competitions/CompetitorsTab'
import { ResultsTab } from '../features/competitions/ResultsTab'
import { ReviewRoutesTab } from '../features/competitions/ReviewRoutesTab'
import { ScorecardTab } from '../features/competitions/ScorecardTab'
import {
  competitionState,
  gymLocalNow,
  type CompetitionDetail,
  type CompetitionEntry,
  type CompetitionResult,
} from '../features/competitions/types'
import type { GymSection } from '../features/routes/types'

const COMPETITION_TABS = [
  'categories',
  'groups',
  'rounds',
  'routes',
  'competitors',
  'results',
  'scorecard',
  'review',
] as const
type CompetitionTab = (typeof COMPETITION_TABS)[number]

export function CompetitionDetailPage() {
  const { competitionId } = useParams<{ competitionId: string }>()
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const { slug, timezone } = useGymContext()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [competition, setCompetition] = useState<CompetitionDetail | null>(null)
  const [entries, setEntries] = useState<CompetitionEntry[]>([])
  const [results, setResults] = useState<CompetitionResult[]>([])
  const [sections, setSections] = useState<GymSection[]>([])
  const [loading, setLoading] = useState(true)
  const [editOpen, setEditOpen] = useState(false)
  // In the URL, so a refresh lands back on the same tab. Groups and rounds never
  // appear together, so only the one this format shows is accepted: a stale
  // ?tab=rounds on a single-day competition falls back instead of selecting
  // a tab that is not there.
  const tabKeys =
    competition?.format === 'single_day'
      ? COMPETITION_TABS.filter((k) => k !== 'rounds')
      : competition?.format === 'long_running'
        ? COMPETITION_TABS.filter((k) => k !== 'groups')
        : COMPETITION_TABS
  const [tab, setTab] = useTabParam<CompetitionTab>('tab', tabKeys, 'categories')
  // Staged round and number changes on the Routes tab. Switching tabs unmounts
  // it and drops them, so a switch waits on a confirm while any are pending.
  const [routesDirty, setRoutesDirty] = useState(false)
  const [pendingTab, setPendingTab] = useState<CompetitionTab | null>(null)
  // Set by "Show competitors" on a category that cannot be removed yet.
  const [competitorCategory, setCompetitorCategory] = useState<string | null>(null)

  function selectTab(next: CompetitionTab) {
    if (tab === 'routes' && next !== 'routes' && routesDirty) setPendingTab(next)
    else setTab(next)
  }

  function discardRouteChanges() {
    if (!pendingTab) return
    setRoutesDirty(false)
    setTab(pendingTab)
    setPendingTab(null)
  }

  const load = useCallback(async () => {
    if (!competitionId) return
    try {
      const token = await getAccessTokenSilently()
      const headers = { Authorization: `Bearer ${token}` }
      const [detailRes, entriesRes, resultsRes, sectionsRes] = await Promise.all([
        fetch(`${apiUrl}/competitions/${competitionId}`, { headers }),
        fetch(`${apiUrl}/competitions/${competitionId}/entries`, { headers }),
        fetch(`${apiUrl}/competitions/${competitionId}/results`, { headers }),
        fetch(`${apiUrl}/gym-sections`, { headers }),
      ])
      if (detailRes.ok) setCompetition((await detailRes.json()) as CompetitionDetail)
      if (entriesRes.ok) setEntries((await entriesRes.json()) as CompetitionEntry[])
      if (resultsRes.ok) setResults((await resultsRes.json()) as CompetitionResult[])
      if (sectionsRes.ok) setSections((await sectionsRes.json()) as GymSection[])
    } finally {
      setLoading(false)
    }
  }, [apiUrl, competitionId, getAccessTokenSilently])

  useEffect(() => {
    void load()
  }, [load])

  if (loading) return <FullPageSpinner />
  if (!competition) {
    return (
      <div className="p-4 sm:p-8">
        <p className="text-foreground/60">{t('competitions.notFound')}</p>
      </div>
    )
  }

  const localNow = gymLocalNow(timezone)
  const state = competitionState(competition, localNow)
  // Both walls' sections for a combined competition.
  const boards = competitionBoards(competition.discipline)
  const disciplineSections = sections.filter((s) =>
    boards.includes(disciplineForSection(s.discipline)),
  )

  return (
    <div className="space-y-6 p-4 sm:p-8">
      <div>
        <Link to={`/${slug}/competitions`} className="text-sm text-primary hover:underline">
          ← {t('competitions.backToList')}
        </Link>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold break-words">{competition.name}</h1>
            <p className="text-foreground/60">
              {t(`competitions.state_${state}`)} ·{' '}
              {t(`competitions.discipline_${competition.discipline}`)} ·{' '}
              {t(`competitions.format_${competition.format}`)}
              {!competition.published && ` · ${t('competitions.draft')}`}
            </p>
          </div>
          <Button size="sm" variant="bordered" onPress={() => setEditOpen(true)}>
            {t('common.edit')}
          </Button>
        </div>
      </div>

      <Tabs
        aria-label={t('competitions.title')}
        selectedKey={tab}
        onSelectionChange={(key) => selectTab(key as CompetitionTab)}
        classNames={{
          base: 'max-w-full',
          tabList: 'max-w-full flex-nowrap overflow-x-auto scrollbar-hide',
        }}
      >
        {/* Categories come first: nobody can enter until at least one exists,
            whatever the format. Groups are time slots and only apply to a
            single-day competition; rounds are blocks of routes and only apply
            to a long-running one, so the two never appear together. */}
        <Tab key="categories" title={t('competitions.tabCategories')}>
          <CategoriesTab
            competitionId={competition.id}
            categories={competition.categories}
            locked={state === 'ended'}
            onRefresh={() => void load()}
            onShowCompetitors={(id) => {
              setCompetitorCategory(id)
              selectTab('competitors')
            }}
          />
        </Tab>
        {competition.format === 'single_day' ? (
          <Tab key="groups" title={t('competitions.tabGroups')}>
            <SessionsTab
              competitionId={competition.id}
              startsAt={competition.starts_at}
              endsAt={competition.ends_at}
              sessions={competition.sessions}
              locked={state === 'ended'}
              onRefresh={() => void load()}
            />
          </Tab>
        ) : (
          <Tab key="rounds" title={t('competitions.tabRounds')}>
            <RoundsTab
              competitionId={competition.id}
              startsAt={competition.starts_at}
              endsAt={competition.ends_at}
              rounds={competition.rounds}
              localNow={localNow}
              locked={state === 'ended'}
              onRefresh={() => void load()}
            />
          </Tab>
        )}
        <Tab key="routes" title={t('competitions.tabRoutes')}>
          <RoutesTab
            competitionId={competition.id}
            discipline={competition.discipline}
            routes={competition.routes}
            rounds={competition.rounds}
            localNow={localNow}
            sections={disciplineSections}
            locked={state === 'ended'}
            results={results}
            onRefresh={() => void load()}
            onDirtyChange={setRoutesDirty}
          />
        </Tab>
        <Tab key="competitors" title={t('competitions.tabCompetitors')}>
          <CompetitorsTab
            entries={entries}
            categories={competition.categories}
            sessions={competition.sessions}
            locked={state === 'ended'}
            requiresConfirmation={competition.requires_confirmation}
            onRefresh={() => void load()}
            initialCategory={competitorCategory}
          />
        </Tab>
        <Tab key="results" title={t('competitions.tabResults')}>
          <ResultsTab
            competitionId={competition.id}
            discipline={competition.discipline}
            sections={disciplineSections}
            results={results}
            entries={entries}
            routes={competition.routes}
            rounds={competition.rounds}
            onRefresh={() => void load()}
          />
        </Tab>
        <Tab key="scorecard" title={t('scorecard.tab')}>
          <ScorecardTab
            competitionId={competition.id}
            categories={competition.categories}
            entries={entries}
            version={results}
          />
        </Tab>
        <Tab key="review" title={t('competitions.tabReview')}>
          <ReviewRoutesTab
            discipline={competition.discipline}
            sections={disciplineSections}
            ended={state === 'ended'}
            labels={Object.fromEntries(
              competition.routes.map((r, i) => [r.route_id, r.label ?? String(i + 1)]),
            )}
            rounds={competition.rounds}
            localNow={localNow}
            roundIds={Object.fromEntries(competition.routes.map((r) => [r.route_id, r.round_id]))}
            routes={competition.provenance_routes}
            onRefresh={() => void load()}
          />
        </Tab>
      </Tabs>

      <Modal
        scrollBehavior="inside"
        isOpen={pendingTab !== null}
        onClose={() => setPendingTab(null)}
      >
        <ModalContent>
          <ModalHeader>{t('competitions.unsavedTitle')}</ModalHeader>
          <ModalBody className="text-sm">
            <p>{t('competitions.unsavedBody')}</p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setPendingTab(null)}>
              {t('competitions.keepEditing')}
            </Button>
            <Button color="danger" onPress={discardRouteChanges}>
              {t('competitions.discardAndLeave')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <CompetitionFormModal
        isOpen={editOpen}
        editTarget={competition}
        onClose={() => setEditOpen(false)}
        onSaved={() => void load()}
      />
    </div>
  )
}
